import { useState } from 'react';
import { submitToWeb3Forms } from '../utils/web3forms.js';
import { track } from '../utils/analytics.js';
import { FEATURES as FEATURE_DATA } from '../data/features.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function FooterSignup() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) {
      setError('Email is required.');
      return;
    }
    if (!EMAIL_RE.test(value)) {
      setError('Enter a valid email address.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await submitToWeb3Forms({
        formName: 'Footer Newsletter',
        subject: `Newsletter signup: ${value}`,
        replyTo: value,
        fields: { Email: value },
      });
      track('newsletter_signup', { location: 'footer' });
      setEmail('');
      setSent(true);
      setTimeout(() => setSent(false), 6000);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`footer__news-wrap${error ? ' has-error' : ''}`}>
      <form className="footer__news-form" noValidate onSubmit={handleSubmit}>
        <span className="footer__news-icon"><i className="ph ph-envelope-simple" aria-hidden="true" /></span>
        <input
          type="email"
          className="footer__news-input"
          placeholder="Enter your email"
          aria-label="Email"
          aria-invalid={!!error}
          value={email}
          onChange={handleChange}
          required
        />
        <button type="submit" className="footer__news-btn" disabled={submitting}>
          {submitting ? 'Sending…' : sent ? 'Subscribed ✓' : 'Subscribe'}
        </button>
      </form>
      {error && (
        <span className="footer__news-error" role="alert">
          <i className="ph-fill ph-warning-circle" aria-hidden="true" /> {error}
        </span>
      )}
      {sent && !error && (
        <span className="footer__news-success" role="status">
          <i className="ph-fill ph-check-circle" aria-hidden="true" /> Thanks — you&rsquo;re on the list.
        </span>
      )}
    </div>
  );
}

export default function Footer() {
  return (
    <div className="aehome">
      <footer className="footer">
        <div className="footer__container">
          <div className="footer__top">
            <div className="footer__newsletter">
              <h2 className="footer__news-title">Stay in the loop</h2>
              <p className="footer__news-sub">
                Get product updates, new features, and tips — straight to your inbox.
              </p>
              <FooterSignup />
            </div>

            <div className="footer__cols">
              <div className="footer__col">
                <h3 className="footer__heading">Features</h3>
                <ul className="footer__list">
                  {FEATURE_DATA.map((f) => (
                    <li key={f.slug}><a href={`/features/${f.slug}`}>{f.label}</a></li>
                  ))}
                </ul>
              </div>

              <div className="footer__col">
                <h3 className="footer__heading">Resources</h3>
                <ul className="footer__list">
                  <li><a href="/help">Help Center</a></li>
                  <li><a href="/blog">Blog</a></li>
                  <li><a href="/roi-calculator">ROI Calculator</a></li>
                  <li><a href="/changelog">Changelog</a></li>
                </ul>
              </div>

              <div className="footer__col">
                <h3 className="footer__heading">Company</h3>
                <ul className="footer__list">
                  <li><a href="/about">About</a></li>
                  <li><a href="/case-studies">Case Studies</a></li>
                  <li><a href="/contact-sales">Contact Sales</a></li>
                  <li><a href="/contact">Contact</a></li>
                </ul>
              </div>

              <div className="footer__col">
                <h3 className="footer__heading">Platform</h3>
                <ul className="footer__list">
                  <li><a href="/pricing">Pricing</a></li>
                  <li><a href="/integrations">Integrations</a></li>
                  <li><a href="/security">Security</a></li>
                  <li><a href="/cookie-settings">Cookie Settings</a></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="footer__bottom">
            <div className="footer__brand">
              <img src="/images/logo.webp" alt="Account Editor" className="footer__logo-img" />
              <span className="footer__copy">© 2026 Account Editor</span>
            </div>

            <div className="footer__bottom-right">
              <div className="footer__legal">
                <a href="/privacy">Privacy Policy</a>
                <span className="footer__legal-sep">|</span>
                <a href="/terms">Terms of Service</a>
              </div>
              <div className="footer__social">
                <a href="#" aria-label="X" className="footer__social-link"><i className="ph ph-x-logo" aria-hidden="true" /></a>
                <a href="https://www.linkedin.com/company/account-editor/" aria-label="LinkedIn" className="footer__social-link" target="_blank" rel="noopener noreferrer"><i className="ph ph-linkedin-logo" aria-hidden="true" /></a>
                <a href="https://www.youtube.com/@accounteditorapp" aria-label="YouTube" className="footer__social-link" target="_blank" rel="noopener noreferrer"><i className="ph ph-youtube-logo" aria-hidden="true" /></a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
